import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CustomContentModal } from '../../../components/CustomContentModal'
import Button from '../../../components/Form/Button'
import { pushToast } from '../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../shared/redux/hooks'
import { uploadTariffHistoryAttachmentApi } from '../../../apis/apis'
import type { TariffHistoryAttachment, TariffRateSheetCategory } from '../../../apis/types'
import { formatDateForApi, formatFileSize } from '../../../util/string'
import { FieldTooltip, PCIA_YEAR_HINT } from './TariffFieldKey'

const MAX_FILE_SIZE = 15 * 1024 * 1024
const ACCEPTED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
]

export const TariffUploadRateSheetModal: React.FC<{
    open: boolean
    historyCuid: string | null
    onClose: () => void
    onUploaded: (attachment: TariffHistoryAttachment) => void
}> = ({ open, historyCuid, onClose, onUploaded }) => {
    const dispatch = useAppDispatch()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [name, setName] = useState('')
    const [documentDate, setDocumentDate] = useState('')
    const [category, setCategory] = useState<TariffRateSheetCategory>('INDIVIDUAL')
    const [vintageYear, setVintageYear] = useState('')
    const [isShared, setIsShared] = useState(false)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)

    const reset = useCallback(() => {
        setName('')
        setDocumentDate(formatDateForApi(new Date()) ?? '')
        setCategory('INDIVIDUAL')
        setVintageYear('')
        setIsShared(false)
        setSelectedFile(null)
        setUploading(false)
    }, [])

    useEffect(() => {
        if (open) reset()
    }, [open, reset])

    const validateFile = (file: File): boolean => {
        const allowed =
            ACCEPTED_TYPES.includes(file.type) ||
            file.name.toLowerCase().endsWith('.pdf') ||
            file.name.toLowerCase().endsWith('.xlsx') ||
            file.name.toLowerCase().endsWith('.xls')
        if (!allowed) {
            void dispatch(pushToast({ type: 'error', message: 'Only PDF or Excel files are allowed' }))
            return false
        }
        if (file.size > MAX_FILE_SIZE) {
            void dispatch(pushToast({ type: 'error', message: 'File must be 15 MB or smaller' }))
            return false
        }
        return true
    }

    const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault()
        if (historyCuid == null || selectedFile == null) return
        const trimmedName = name.trim()
        if (trimmedName === '') {
            void dispatch(pushToast({ type: 'error', message: 'Document name is required' }))
            return
        }

        setUploading(true)
        try {
            const attachment = await uploadTariffHistoryAttachmentApi(historyCuid, {
                file: selectedFile,
                label: trimmedName,
                category,
                documentDate: documentDate !== '' ? documentDate : undefined,
                vintageYear: vintageYear.trim() !== '' ? vintageYear.trim() : null,
                isShared,
            })
            onUploaded(attachment)
            void dispatch(pushToast({ type: 'success', message: 'Rate sheet uploaded' }))
            onClose()
        } catch {
            void dispatch(pushToast({ type: 'error', message: 'Failed to upload rate sheet' }))
        } finally {
            setUploading(false)
        }
    }

    return (
        <CustomContentModal isOpen={open} onClose={() => !uploading && onClose()}>
            <form
                onSubmit={(e) => void handleSubmit(e)}
                className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-900"
            >
                <h3 className="text-lg font-medium text-slate-900 dark:text-white">Upload rate sheet</h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    PDF or Excel. Link to this rateset; optionally share with other tariffs on the same utility.
                </p>

                <div className="mt-4 space-y-3">
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Name
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                            placeholder="e.g. PG&E B19 rate sheet"
                            required
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Category
                        </label>
                        <select
                            value={category}
                            onChange={(e) => {
                                const next = e.target.value as TariffRateSheetCategory
                                setCategory(next)
                                if (next === 'GROUP') setIsShared(true)
                            }}
                            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        >
                            <option value="INDIVIDUAL">Individual</option>
                            <option value="GROUP">Group</option>
                        </select>
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                            <FieldTooltip content={PCIA_YEAR_HINT}>PCIA year (optional)</FieldTooltip>
                        </label>
                        <input
                            type="text"
                            value={vintageYear}
                            onChange={(e) => setVintageYear(e.target.value)}
                            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                            placeholder="e.g. 2024"
                        />
                    </div>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                            Document date
                        </label>
                        <input
                            type="date"
                            value={documentDate}
                            onChange={(e) => setDocumentDate(e.target.value)}
                            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                        <input
                            type="checkbox"
                            checked={isShared}
                            onChange={(e) => setIsShared(e.target.checked)}
                            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        Share with other tariffs on this utility
                    </label>
                    <div>
                        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                            File
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,.xlsx,.xls,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-800 dark:text-slate-300 dark:file:bg-slate-800 dark:file:text-slate-200"
                            onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file != null && validateFile(file)) {
                                    setSelectedFile(file)
                                    if (name.trim() === '') {
                                        setName(file.name.replace(/\.[^.]+$/, ''))
                                    }
                                }
                            }}
                        />
                        {selectedFile != null ? (
                            <p className="mt-1 text-xs text-slate-500">
                                {selectedFile.name} · {formatFileSize(selectedFile.size)}
                            </p>
                        ) : null}
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    <Button type="button" color="slate" disabled={uploading} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={uploading || selectedFile == null}>
                        {uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                </div>
            </form>
        </CustomContentModal>
    )
}
